import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';

/**
 * 정산 도매가.
 * 공급사(giftItemBrand) 입장에선 상조별로 달라지는 GiftItem.priceWhole 오버라이드가
 * 아니라, 우리와 맺은 계약 단가인 giftItemInfo.priceWhole(상조 무관 단일 단가)를 본다.
 */
export const getSettlementWholePrice = (delivery: GiftDeliveryForAdmin): number => {
  if (typeof delivery.giftItem === 'object') {
    const info = (delivery.giftItem as GiftItemForStore).giftItemInfo;
    if (info && typeof info === 'object' && typeof info.priceWhole === 'number') {
      return info.priceWhole;
    }
  }
  return 0;
};

/** 배송이 속한 공급사(giftItemBrand) id */
export const getSettlementBrandId = (
  delivery: GiftDeliveryForAdmin,
): string | undefined => {
  const brand = delivery.giftItemBrand;
  if (brand && typeof brand === 'object') return brand._id;
  if (typeof brand === 'string') return brand;
  return undefined;
};

/** 배송 1건의 정산 내역 분해 */
export interface SettlementLine {
  delivery: GiftDeliveryForAdmin;
  /** 정산 수량 */
  quantity: number;
  /** 도매 개당 단가 (giftItemInfo.priceWhole) */
  wholePrice: number;
  /** 도매가 × 수량 */
  wholeAmount: number;
  /** 적용된 소량 추가 개당 단가 (미적용 시 0) */
  extraUnitPrice: number;
  /** 소량주문 추가단가 합 (면제수량 미만일 때만) */
  extraUnitAmount: number;
  /** 소량 추가단가 면제 기준수량 (이 수량 이상이면 추가단가 무료, 0=정책 없음) */
  freeShippingMinQuantity: number;
  /** 공급사 정산정보상 건당 택배비 (면제/무료 적용 전 원단가) */
  courierFeeBase: number;
  /** 이 건에 실제 부과된 택배비 (묶음 면제/무료수량 적용 후) */
  courierFee: number;
  /** 택배비 무료 기준수량 (묶음 합계수량이 이 이상이면 무료, 0=정책 없음) */
  courierFreeMinQuantity: number;
  /** 정산금액 합 */
  total: number;
  /** 해당 공급사 정산정보(settlementInfo) 존재 여부 */
  hasSettlementInfo: boolean;
  /** 같은 송장번호 묶음으로 택배비가 면제된 건 */
  courierBundled: boolean;
  /** 묶음 합계수량이 무료기준 충족이라 택배비가 무료가 된 건 */
  courierFreeByQuantity: boolean;
}

/**
 * 배송완료 목록 전체의 정산 내역을 계산한다.
 *
 * - 도매: giftItemInfo.priceWhole × 수량
 * - 추가단가: 수량 < freeShippingMinQuantity 이면 shippingFee × 수량
 * - 택배비: 같은 송장번호(trackingNumber) = 한 박스로 보고 묶음당 courierFee 1회.
 *   같은 송장은 곧 같은 발송 건이므로, 묶음 중 첫 건에만 부과하고 나머지는 면제한다.
 *   · 묶음 합계수량 ≥ courierFreeMinQuantity 이면 무료
 *
 * @param settlementByBrand 공급사 id → 정산정보 조회 함수
 */
export const computeSettlementLines = (
  deliveries: GiftDeliveryForAdmin[],
  settlementByBrand: (brandId?: string) => SettlementInfo | undefined,
): SettlementLine[] => {
  // 송장번호 → 같은 박스로 묶인 배송들
  const groupByTracking = new Map<string, GiftDeliveryForAdmin[]>();
  for (const d of deliveries) {
    const tracking = d.shipping?.trackingNumber?.trim();
    if (!tracking) continue;
    const group = groupByTracking.get(tracking);
    if (group) group.push(d);
    else groupByTracking.set(tracking, [d]);
  }

  return deliveries.map((d) => {
    const settlement = settlementByBrand(getSettlementBrandId(d));
    const qty = d.quantity || 0;
    const wholePrice = getSettlementWholePrice(d);
    const wholeAmount = wholePrice * qty;

    const shippingFee = settlement?.shippingFee ?? 0;
    const freeShippingMinQuantity = settlement?.freeShippingMinQuantity ?? 0;
    const courierFee = settlement?.courierFee ?? 0;
    const courierFreeMinQuantity = settlement?.courierFreeMinQuantity ?? 0;

    // 소량주문 추가단가: 면제수량 미만일 때만 개당 가산
    const extraApplies = freeShippingMinQuantity > 0 && qty < freeShippingMinQuantity;
    const extraUnitPrice = extraApplies ? shippingFee : 0;
    const extraUnitAmount = extraUnitPrice * qty;

    // 택배비: 같은 송장번호 = 한 박스로 보고 묶음당 1회만 부과
    let charged = 0;
    let courierBundled = false;
    let courierFreeByQuantity = false;
    if (courierFee > 0) {
      const tracking = d.shipping?.trackingNumber?.trim();
      const group = tracking ? groupByTracking.get(tracking) ?? [d] : [d];
      const groupQty = group.reduce((sum, g) => sum + (g.quantity || 0), 0);

      if (courierFreeMinQuantity > 0 && groupQty >= courierFreeMinQuantity) {
        // 묶음 합계수량이 무료기준 충족 → 택배비 0
        courierFreeByQuantity = true;
      } else if (tracking && group[0]?._id !== d._id) {
        // 묶음 첫 건만 택배비 부과, 나머지는 면제
        courierBundled = true;
      } else {
        charged = courierFee;
      }
    }

    return {
      delivery: d,
      quantity: qty,
      wholePrice,
      wholeAmount,
      extraUnitPrice,
      extraUnitAmount,
      freeShippingMinQuantity,
      courierFeeBase: courierFee,
      courierFee: charged,
      courierFreeMinQuantity,
      total: wholeAmount + extraUnitAmount + charged,
      hasSettlementInfo: !!settlement,
      courierBundled,
      courierFreeByQuantity,
    };
  });
};

/** 통화 표기 (원) */
export const formatWon = (amount: number): string => `${amount.toLocaleString()}원`;

/** 최종합계 10원 올림 (라인은 원 단위 그대로, 합계 표시 시점에만 1회 적용) */
export const roundUpTo10 = (amount: number): number => Math.ceil(amount / 10) * 10;
